// ============================================================================
// AUDIT.JS - Audit Log Visibility & Governance
// 
// Core Principle: One underlying audit log schema per decision.
// Visibility is role-based and specialty-scoped, not duplicated per role.
// The system produces one truth, then filters exposure.
// ============================================================================

/**
 * Audit View Levels
 * 
 * FULL - Specialist, optional Tenant Admin (when enabled)
 *   - outcome, rationale, triggeredRedFlags, missingRequired
 *   - rule fires, overrides, trace events
 *   - packId + versionId + engineVersion
 *   - artifact references, timestamps
 * 
 * MEDIUM - Tenant Admin default
 *   - outcome, triggeredRedFlags (IDs + labels), missingRequired
 *   - packId + versionId, completenessScore, timestamps
 *   - EXCLUDES: rule IDs, trace event details, override mechanics
 * 
 * MINIMAL - GP/Staff (for UI feedback only, not audit log access)
 *   - outcome, missingRequired (labels only), urgency flag
 *   - EXCLUDES: rule IDs, trace, versions, rationale details
 * 
 * NONE - No audit log access
 */
export const AUDIT_VIEW_LEVELS = {
  FULL: 'FULL',
  MEDIUM: 'MEDIUM',
  MINIMAL: 'MINIMAL',
  NONE: 'NONE',
};

/**
 * Determine the audit view level for a given role
 * 
 * @param {string} role - User role
 * @param {Object} options - Additional options
 * @param {boolean} options.tenantAdminFullAccess - If tenant admin has full per-case access enabled
 * @returns {string} View level from AUDIT_VIEW_LEVELS
 */
export const getAuditViewLevel = (role, options = {}) => {
  switch (role) {
    case 'specialist':
      // Specialists always get FULL access to their specialty's logs
      return AUDIT_VIEW_LEVELS.FULL;
    
    case 'tenant_admin':
      // Tenant admins get MEDIUM by default, FULL if explicitly enabled
      return options.tenantAdminFullAccess 
        ? AUDIT_VIEW_LEVELS.FULL 
        : AUDIT_VIEW_LEVELS.MEDIUM;
    
    case 'gp_employee':
      // GPs do NOT get audit log access
      // They only receive completion feedback in UI (not via audit docs)
      return AUDIT_VIEW_LEVELS.NONE;
    
    case 'platform_admin':
      // Platform admins NEVER see per-case audit logs
      // They only see system health metrics, not clinical data
      return AUDIT_VIEW_LEVELS.NONE;
    
    default:
      return AUDIT_VIEW_LEVELS.NONE;
  }
};

/**
 * Filter an audit log based on view level
 * Returns a filtered copy of the audit log appropriate for the view level
 * 
 * @param {string} viewLevel - One of AUDIT_VIEW_LEVELS
 * @param {Object} auditLog - The full audit log document
 * @returns {Object|null} Filtered audit log or null if no access
 */
export const filterAuditLog = (viewLevel, auditLog) => {
  if (!auditLog) return null;
  
  switch (viewLevel) {
    case AUDIT_VIEW_LEVELS.FULL:
      // Return everything
      return auditLog;
    
    case AUDIT_VIEW_LEVELS.MEDIUM:
      // Return governance-level view without trace details
      return {
        type: auditLog.type,
        runId: auditLog.runId,
        tenantId: auditLog.tenantId,
        specialtyId: auditLog.specialtyId,
        
        // Context (limited)
        context: {
          packId: auditLog.context?.packId,
          packName: auditLog.context?.packName,
          versionId: auditLog.context?.versionId,
          specialtyId: auditLog.context?.specialtyId,
          // EXCLUDE: engineVersion, attemptNumber, previousEventId
        },
        
        // Timestamps
        timestamps: auditLog.timestamps,
        
        // Evaluation (limited)
        evaluation: {
          outcome: auditLog.evaluation?.outcome,
          completenessScore: auditLog.evaluation?.completenessScore,
          triggeredRedFlags: auditLog.evaluation?.triggeredRedFlags || [],
          missingRequired: auditLog.evaluation?.missingRequired || [],
          // EXCLUDE: hasOverride, overrideReason, overrideTrigger, rationale.reasonCodes
          rationale: {
            summary: auditLog.evaluation?.rationale?.summary,
            // EXCLUDE: full, reasonCodes
          },
        },
        
        // EXCLUDE: inputs, artifacts, trace, actor
        
        createdAt: auditLog.createdAt,
      };
    
    case AUDIT_VIEW_LEVELS.MINIMAL:
      // Return minimal feedback view
      return {
        runId: auditLog.runId,
        evaluation: {
          outcome: auditLog.evaluation?.outcome,
          missingRequired: (auditLog.evaluation?.missingRequired || []).map(f => ({
            label: f.label, // Labels only, no IDs
          })),
          isUrgent: auditLog.evaluation?.outcome === 'Urgent',
        },
        timestamps: {
          completed: auditLog.timestamps?.completed,
        },
        // EXCLUDE: everything else
      };
    
    case AUDIT_VIEW_LEVELS.NONE:
    default:
      // No access
      return null;
  }
};

/**
 * Get filtered audit view for a role
 * Convenience function combining getAuditViewLevel and filterAuditLog
 * 
 * @param {string} role - User role
 * @param {Object} auditLog - The full audit log document
 * @param {Object} options - Additional options
 * @returns {Object|null} Filtered audit log or null if no access
 */
export const getAuditView = (role, auditLog, options = {}) => {
  const viewLevel = getAuditViewLevel(role, options);
  return filterAuditLog(viewLevel, auditLog);
};

/**
 * Check if a role can access audit logs at all
 * 
 * @param {string} role - User role
 * @returns {boolean}
 */
export const canAccessAuditLogs = (role) => {
  return getAuditViewLevel(role) !== AUDIT_VIEW_LEVELS.NONE;
};

/**
 * Get GP feedback from a decision result (NOT from audit log)
 * This is what GPs see in the UI after running the gate
 * 
 * @param {Object} decision - The decision result from evaluateReferral
 * @returns {Object} GP-safe feedback object
 */
export const getGpFeedback = (decision) => {
  return {
    outcome: decision.outcome,
    isUrgent: decision.outcome === 'Urgent',
    isComplete: decision.outcome !== 'Not Yet',
    completenessScore: decision.completenessScore,
    
    // Missing fields (labels only, no IDs for security)
    missingFields: (decision.missingRequired || []).map(f => f.label),
    
    // Next steps (for Not Yet outcome)
    nextSteps: decision.nextSteps || [],
    
    // Messages
    patientMessage: decision.patientMessage,
    
    // Can re-run?
    canRerunGate: decision.canRerunGate || false,
    rerunInstructions: decision.rerunInstructions || null,
    
    // EXCLUDED: rationale, triggeredRedFlags (IDs), audit trace, pack version, engine version
  };
};

// ============================================================================
// TENANT SECURITY SETTINGS HELPERS
// ============================================================================

/**
 * Default tenant security settings
 */
export const DEFAULT_SECURITY_SETTINGS = {
  allowPerCaseAuditRead: false,
  allowedAuditSpecialtyIds: [],
};

/**
 * Check if tenant admin can access audit logs for a specialty
 * 
 * @param {Object} securitySettings - Tenant security settings
 * @param {string} specialtyId - Specialty to check
 * @returns {boolean}
 */
export const canTenantAdminAccessAudit = (securitySettings, specialtyId) => {
  if (!securitySettings) return false;
  if (!securitySettings.allowPerCaseAuditRead) return false;
  if (!specialtyId) return false;
  
  const allowedSpecialties = securitySettings.allowedAuditSpecialtyIds || [];
  return allowedSpecialties.includes(specialtyId);
};

/**
 * Create security settings document
 * 
 * @param {Object} options
 * @returns {Object} Security settings document
 */
export const createSecuritySettings = ({
  allowPerCaseAuditRead = false,
  allowedAuditSpecialtyIds = [],
} = {}) => {
  return {
    allowPerCaseAuditRead,
    allowedAuditSpecialtyIds: Array.isArray(allowedAuditSpecialtyIds) 
      ? allowedAuditSpecialtyIds 
      : [],
    updatedAt: new Date().toISOString(),
  };
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  AUDIT_VIEW_LEVELS,
  getAuditViewLevel,
  filterAuditLog,
  getAuditView,
  canAccessAuditLogs,
  getGpFeedback,
  DEFAULT_SECURITY_SETTINGS,
  canTenantAdminAccessAudit,
  createSecuritySettings,
};
